// Two-factor sign-in (#227), the pieces the CMS admin and the console share: the code field, the QR code (drawn here
// from the otpauth address, never by an outside service), the set-up form and the recovery codes. Text comes from the
// app as `labels`, in its own languages.

import { groupKey, qrPath } from '@bak/shared-utils'
import { type FormEvent, useMemo, useState } from 'react'
import { FieldError, Input, Label, Text, TextField } from 'react-aria-components'

import { Button } from './Button'
import { INPUT } from './fields'

export interface TwoFactorLabels {
  /** "Scan this QR code with an authenticator app…, then enter the 6-digit code it shows." */
  setupIntro: string
  qr: string
  manualKey: string
  code: string
  turnOn: string
  working: string
  recoveryTitle: string
  recoveryIntro: string
  copy: string
  copied: string
  download: string
  saved: string
}

/** Six digits or a recovery code: numeric keyboard on phones, and the browser's one-time-code autofill. */
export function CodeField({ label, value, onChange, hint, error, autoFocus }: { label: string; value: string; onChange: (v: string) => void; hint?: string; error?: string | null; autoFocus?: boolean }) {
  return (
    <TextField value={value} onChange={onChange} isInvalid={!!error} isRequired maxLength={20} className="flex flex-col gap-1">
      <Label className="text-xs font-medium text-[var(--fg-muted)]">{label}</Label>
      <Input className={`${INPUT} font-mono tracking-widest`} inputMode="numeric" autoComplete="one-time-code" autoCapitalize="off" spellCheck={false} {...(autoFocus ? { autoFocus } : {})} />
      {error ? <FieldError className="text-xs text-[var(--color-danger)]">{error}</FieldError> : hint ? <Text slot="description" className="text-[11px] text-[var(--fg-quiet)]">{hint}</Text> : null}
    </TextField>
  )
}

export function TwoFactorQr({ uri, label }: { uri: string; label: string }) {
  const { size, d } = useMemo(() => qrPath(uri), [uri])
  return (
    <svg viewBox={`0 0 ${size} ${size}`} role="img" aria-label={label} shapeRendering="crispEdges" className="h-48 w-48 rounded-lg border border-[var(--mv-border)] bg-white p-1">
      <rect width={size} height={size} fill="#fff" />
      <path fill="#000" d={d} />
    </svg>
  )
}

/** QR code, the key to type by hand, and the first code from the app. The parent sends the code and shows `error`. */
export function TwoFactorSetupForm({ setup, labels, onSubmit, error, pending }: { setup: { secret: string; uri: string }; labels: TwoFactorLabels; onSubmit: (code: string) => void; error?: string | null; pending?: boolean }) {
  const [code, setCode] = useState('')
  const submit = (e: FormEvent) => {
    e.preventDefault()
    onSubmit(code)
  }
  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <p className="text-sm text-[var(--fg-muted)]">{labels.setupIntro}</p>
      <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-start">
        <TwoFactorQr uri={setup.uri} label={labels.qr} />
        <div className="min-w-0 flex-1">
          <p className="text-xs text-[var(--fg-muted)]">{labels.manualKey}</p>
          <code className="mt-1 block rounded-md bg-[var(--mv-bg)] px-2 py-1.5 font-mono text-sm font-semibold tracking-wide break-all text-[var(--mv-fg-strong)]">{groupKey(setup.secret)}</code>
        </div>
      </div>
      <CodeField label={labels.code} value={code} onChange={setCode} error={error ?? null} />
      <Button type="submit" isDisabled={pending || code.trim().length < 6} className="w-full sm:w-auto sm:self-end">
        {pending ? labels.working : labels.turnOn}
      </Button>
    </form>
  )
}

/** The ten recovery codes, shown once, with Copy and Download (a text file made in the browser). */
export function RecoveryCodes({ codes, labels, onDone, fileName = 'digimasi-recovery-codes.txt' }: { codes: string[]; labels: TwoFactorLabels; onDone: () => void; fileName?: string }) {
  const [copied, setCopied] = useState(false)
  const text = `${codes.join('\n')}\n`
  const download = () => {
    const a = document.createElement('a')
    a.href = URL.createObjectURL(new Blob([text], { type: 'text/plain' }))
    a.download = fileName
    a.click()
    setTimeout(() => URL.revokeObjectURL(a.href), 1000)
  }
  return (
    <div className="flex flex-col gap-4">
      <div>
        {labels.recoveryTitle && <p className="mb-1 font-semibold text-[var(--mv-fg-strong)]">{labels.recoveryTitle}</p>}
        <p className="text-sm text-[var(--fg-muted)]">{labels.recoveryIntro}</p>
      </div>
      <ol className="grid list-decimal grid-cols-2 gap-x-6 gap-y-1 rounded-lg border border-[var(--mv-border)] bg-[var(--mv-bg)] py-3 pr-3 pl-8 font-mono text-sm font-semibold text-[var(--mv-fg-strong)]">
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ol>
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" size="sm" onPress={() => void navigator.clipboard.writeText(text).then(() => setCopied(true))}>
          {copied ? labels.copied : labels.copy}
        </Button>
        <Button variant="secondary" size="sm" onPress={download}>
          {labels.download}
        </Button>
        <Button className="ml-auto" size="sm" onPress={onDone}>
          {labels.saved}
        </Button>
      </div>
    </div>
  )
}
